// frontend/src/components/AnonymousChat.js
// مكون المحادثة المجهولة — قائمة الجلسات + نافذة الشات
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, ArrowRight, User, Shield, MessageSquare, Inbox, Trash2, Check, CheckCheck, Clock, AlertTriangle, Eye, EyeOff, Smile, Flag, Search } from 'lucide-react';
import ReportModal from './ReportModal';
import './AnonymousChat.css';
import { useChat } from '../context/ChatContext';
import useTelegramBackButton from '../hooks/useTelegramBackButton';

const AnonymousChat = ({ user, deepLinkCode, onBack, isSubscribed, onRequestSubscription }) => {
  const { socket } = useChat();
  const [view, setView] = useState(deepLinkCode ? 'chat' : 'sessions'); // 'sessions' أو 'chat'
  const [sessionsTab, setSessionsTab] = useState('received'); // 'received' أو 'sent'
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const emojis = ['😊', '😂', '❤️', '👍', '😍', '🔥', '🎉', '😭', '🤔', '🙌', '👏', '✨', '🚀', '🌟', '💯', '👀', '🌹', '💔', '🎈', '✌️'];
  
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [activeOptionMenuMessageId, setActiveOptionMenuMessageId] = useState(null);
  const touchStartX = useRef(0);
  const swipedMessageRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipedMessageId, setSwipedMessageId] = useState(null);

  const sortSessionsByActivity = useCallback((list) => {
    return [...list].sort((a, b) => {
      const aTs = a.lastMessageTimestamp ? new Date(a.lastMessageTimestamp).getTime() : 0;
      const bTs = b.lastMessageTimestamp ? new Date(b.lastMessageTimestamp).getTime() : 0;
      return bTs - aTs;
    });
  }, []);

  // Swipe-to-reply touch events
  const handleTouchStart = (e, msg) => {
    touchStartX.current = e.touches[0].clientX;
    swipedMessageRef.current = msg;
    setSwipedMessageId(msg.id || msg.dbId);
  };

  const handleTouchMove = (e) => {
    if (!swipedMessageRef.current) return;
    const currentX = e.touches[0].clientX;
    const diffX = touchStartX.current - currentX;
    if (diffX > 0) {
      const offset = Math.min(diffX, 80);
      setSwipeOffset(offset);
    }
  };

  const handleTouchEnd = () => {
    if (swipedMessageRef.current && swipeOffset >= 50) {
      const msg = swipedMessageRef.current;
      const isOwner = activeSession?.ownerTelegramId === userId;
      const currentRole = isOwner ? 'owner' : 'visitor';
      const pName = isOwner
        ? activeSession?.visitorDisplayName
        : (ownerInfo?.nickname || activeSession?.ownerNickname || 'مستخدم');
      setReplyingToMessage({
        id: msg.id || msg.dbId,
        text: msg.text,
        senderName: msg.senderRole === currentRole ? 'أنا' : (pName || 'الطرف الآخر')
      });
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
    }
    setSwipeOffset(0);
    swipedMessageRef.current = null;
    setSwipedMessageId(null);
  };

  const handleReplySnippetClick = (replyToId) => {
    const element = document.getElementById(`msg-${replyToId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('message-highlight');
      setTimeout(() => {
        element.classList.remove('message-highlight');
      }, 1500);
    }
  };

  const userId = user?.id || user?.telegramId;

  // التمرير
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Deep link: فتح محادثة مجهولة مباشرة
  useEffect(() => {
    if (!deepLinkCode || !userId || !socket) return;

    const initDeepLink = async () => {
      if (!isSubscribed) {
        if (onRequestSubscription) onRequestSubscription();
        setError('يرجى الاشتراك في القناة للمتابعة والبدء بالمحادثات المجهولة.');
        return;
      }
      setChatLoading(true);
      try {
        // جلب معلومات صاحب الرابط
        const ownerRes = await fetch(`/api/anonymous/owner/${deepLinkCode}`);
        if (!ownerRes.ok) {
          setError('الرابط غير صالح أو المستخدم غير موجود');
          setChatLoading(false);
          return;
        }
        const ownerData = await ownerRes.json();
        setOwnerInfo(ownerData.owner);

        // إنشاء/استئناف الجلسة
        const sessionRes = await fetch('/api/anonymous/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ownerUniqueCode: deepLinkCode,
            visitorTelegramId: userId
          })
        });

        if (sessionRes.ok) {
          const sessionData = await sessionRes.json();
          const session = sessionData.session;
          setActiveSession(session);

          // جلب الرسائل السابقة
          const msgRes = await fetch(`/api/anonymous/session/${session._id}/messages?readerRole=visitor`);
          if (msgRes.ok) {
            const msgData = await msgRes.json();
            setMessages((msgData.messages || []).map(m => ({
              id: m._id,
              senderRole: m.senderRole,
              text: m.text,
              timestamp: m.timestamp,
              status: m.status || 'read',
              replyTo: m.replyTo || null
            })));
          }

          // الانضمام للسوكيت
          socket.emit('anon_chat_init', {
            ownerUniqueCode: deepLinkCode,
            visitorTelegramId: userId
          });

          setView('chat');
        } else {
          const errData = await sessionRes.json();
          setError(errData.error || 'حدث خطأ');
        }
      } catch (err) {
        console.error('Deep link error:', err);
        setError('حدث خطأ في الاتصال');
      } finally {
        setChatLoading(false);
      }
    };

    initDeepLink();
  }, [deepLinkCode, userId, socket]);

  // جلب قائمة الجلسات
  useEffect(() => {
    if (view !== 'sessions' || !userId) return;

    const fetchSessions = async () => {
      setLoading(true);
      try {
        let endpoint;
        if (sessionsTab === 'received') {
          endpoint = `/api/anonymous/${userId}/sessions`;
        } else if (sessionsTab === 'sent') {
          endpoint = `/api/anonymous/${userId}/visitor-sessions`;
        } else if (sessionsTab === 'match') {
          endpoint = `/api/chats/${userId}/match-history`;
        }

        const res = await fetch(endpoint);
        if (res.ok) {
          const data = await res.json();
          setSessions(sortSessionsByActivity(data.sessions || []));
        }
      } catch (err) {
        console.error('Error fetching sessions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [view, sessionsTab, userId, sortSessionsByActivity]);

  // Socket events للمحادثة المجهولة
  useEffect(() => {
    if (!socket) return;

    const joinRoom = () => {
      if (!activeSession) return;
      if (activeSession.ownerTelegramId === userId) {
        socket.emit('anon_chat_owner_join', {
          sessionId: activeSession._id.toString(),
          ownerTelegramId: userId
        });
      } else {
        socket.emit('anon_chat_init', {
          ownerUniqueCode: activeSession.ownerUniqueCode,
          visitorTelegramId: userId
        });
      }
    };

    if (socket.connected) {
      joinRoom();
    }

    socket.on('connect', joinRoom);

    const handleReceive = (msg) => {
      if (activeSession && msg.sessionId === activeSession._id?.toString()) {
        setMessages(prev => [...prev, {
          id: msg.id,
          senderRole: msg.senderRole,
          text: msg.text,
          timestamp: msg.timestamp,
          status: 'delivered',
          replyTo: msg.replyTo || null
        }]);
        setIsTyping(false);
        socket.emit('anon_message_read', { id: msg.id });
      }
    };

    const handleTyping = () => {
      setIsTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
    };

    const handleStatus = ({ id, status }) => {
      setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));
    };

    const handleError = ({ message }) => {
      setError(message);
    };

    const handleSessionActivity = ({ sessionId, senderRole, text, timestamp }) => {
      if (view !== 'sessions') return;

      const currentTabRole = sessionsTab === 'received' ? 'owner' : 'visitor';
      setSessions(prev => sortSessionsByActivity(prev.map(session => {
        if (String(session._id) !== String(sessionId)) return session;

        const isIncoming = senderRole !== currentTabRole;
        return {
          ...session,
          lastMessage: text,
          lastMessageTimestamp: timestamp || new Date().toISOString(),
          unreadCount: isIncoming ? (session.unreadCount || 0) + 1 : (session.unreadCount || 0)
        };
      })));
    };

    socket.on('anon_chat_receive', handleReceive);
    socket.on('anon_chat_typing', handleTyping);
    socket.on('anon_message_status', handleStatus);
    socket.on('anon_chat_error', handleError);
    socket.on('anon_session_activity', handleSessionActivity);

    return () => {
      socket.off('connect', joinRoom);
      socket.off('anon_chat_receive', handleReceive);
      socket.off('anon_chat_typing', handleTyping);
      socket.off('anon_message_status', handleStatus);
      socket.off('anon_chat_error', handleError);
      socket.off('anon_session_activity', handleSessionActivity);
    };
  }, [socket, activeSession, view, sessionsTab, sortSessionsByActivity, userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // فتح جلسة موجودة
  const openSession = async (session) => {
    if (!isSubscribed) {
      if (onRequestSubscription) onRequestSubscription();
      return;
    }
    setChatLoading(true);
    try {
      if (sessionsTab === 'match') {
        const res = await fetch(`/api/chats/${userId}/${session.partnerId}`);
        if (res.ok) {
          const data = await res.json();
          setMessages((data.history || []).map(m => ({
            id: m.id,
            senderRole: m.sender === 'me' ? 'visitor' : 'owner', // map to visitor/owner so it renders in normal Chat bubbles
            text: m.text,
            timestamp: m.timestamp,
            status: m.status || 'read',
            replyTo: m.replyTo || null
          })));
        }

        setActiveSession({
          _id: session.roomId || session._id,
          isMatch: true,
          partnerId: session.partnerId,
          partnerName: session.partnerName,
          partnerAvatarUrl: session.partnerAvatarUrl,
          partnerIsVip: session.partnerIsVip,
          partnerCustomNameColor: session.partnerCustomNameColor,
          areFriends: session.areFriends
        });
        setSessions(prev => prev.map(s => (
          String(s._id) === String(session._id) ? { ...s, unreadCount: 0 } : s
        )));
        setView('chat');
        return;
      }

      const readerRole = sessionsTab === 'received' ? 'owner' : 'visitor';
      const res = await fetch(`/api/anonymous/session/${session._id}/messages?readerRole=${readerRole}`);
      if (res.ok) {
        const data = await res.json();
        setMessages((data.messages || []).map(m => ({
          id: m._id,
          senderRole: m.senderRole,
          text: m.text,
          timestamp: m.timestamp,
          status: m.status || 'read',
          replyTo: m.replyTo || null
        })));
      }

      setActiveSession(session);
      setSessions(prev => prev.map(s => (
        String(s._id) === String(session._id) ? { ...s, unreadCount: 0 } : s
      )));
      setView('chat');

      // الانضمام للسوكيت حسب الدور
      if (sessionsTab === 'received') {
        socket.emit('anon_chat_owner_join', {
          sessionId: session._id.toString(),
          ownerTelegramId: userId
        });
      } else {
        socket.emit('anon_chat_init', {
          ownerUniqueCode: session.ownerUniqueCode,
          visitorTelegramId: userId
        });
      }
    } catch (err) {
      console.error('Error opening session:', err);
    } finally {
      setChatLoading(false);
    }
  };

  // حذف جلسة
  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('هل تريد حذف هذه المحادثة المجهولة؟')) return;

    try {
      await fetch(`/api/anonymous/session/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      setSessions(prev => prev.filter(s => s._id !== sessionId));
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!isSubscribed) {
      if (onRequestSubscription) onRequestSubscription();
      return;
    }
    if (!inputText.trim() || !socket || !activeSession) return;

    const myRole = activeSession.ownerTelegramId === userId ? 'owner' : 'visitor';
    const msgId = Date.now();

    setMessages(prev => [...prev, {
      id: msgId,
      senderRole: myRole,
      text: inputText,
      timestamp: new Date().toISOString(),
      status: 'sending',
      replyTo: replyingToMessage
    }]);

    socket.emit('anon_chat_message', { id: msgId, text: inputText, replyTo: replyingToMessage });
    setInputText('');
    setShowEmojiPicker(false);
    setReplyingToMessage(null);
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    if (socket) socket.emit('anon_chat_typing');
  };

  // إرسال بلاغ عن محادثة مجهولة
  const handleReport = async (reason) => {
    try {
      const reportedId = myRole === 'owner'
        ? activeSession?.visitorTelegramId
        : activeSession?.ownerTelegramId;

      const reportData = {
        reporterId: userId,
        reportedEntityId: reportedId || 'unknown',
        entityType: 'anonymous_chat',
        reason,
        messages: messages.slice(-10)
      };

      const res = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData)
      });

      if (res.ok) alert('تم إرسال البلاغ بنجاح. شكراً لك.');
      else alert('حدث خطأ أثناء إرسال البلاغ.');
    } catch (e) {
      console.error('Error submitting anonymous report', e);
    }
  };

  const handleBackToSessions = useCallback(() => {
    if (socket) socket.emit('anon_chat_leave');
    setActiveSession(null);
    setMessages([]);
    setOwnerInfo(null);
    setError(null);
    setView('sessions');
  }, [socket]);

  const isNativeBackActive = useTelegramBackButton(
    view === 'chat' ? handleBackToSessions : onBack
  );

  // تحديد الدور في الجلسة الحالية
  const myRole = activeSession?.ownerTelegramId === userId ? 'owner' : 'visitor';

  // حالة الخطأ
  if (error && view === 'chat' && !activeSession) {
    return (
      <div className="anon-chat-container">
        <div className="anon-error-screen">
          <AlertTriangle size={48} />
          <h3>خطأ</h3>
          <p>{error}</p>
          <button className="btn-back" onClick={onBack || handleBackToSessions}>
            عودة
          </button>
        </div>
      </div>
    );
  }

  // حالة التحميل
  if (chatLoading) {
    return (
      <div className="anon-chat-container">
        <div className="anon-loading-screen">
          <div className="loader-pulse-purple"></div>
          <p>جاري تحميل المحادثة...</p>
        </div>
      </div>
    );
  }

  // عرض المحادثة النشطة
  if (view === 'chat' && activeSession) {
    const partnerName = activeSession.isMatch
      ? activeSession.partnerName
      : (myRole === 'owner'
        ? activeSession.visitorDisplayName
        : (ownerInfo?.nickname || activeSession.ownerNickname || 'مستخدم'));

    return (
      <div className="anon-chat-container">
        <div className="anon-chat-header">
          {!isNativeBackActive && (
            <button className="back-btn-round" onClick={handleBackToSessions} aria-label="عودة"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#999' }}>
              <ArrowRight size={20} />
            </button>
          )}
          <div className="anon-partner-avatar">
            {activeSession.isMatch ? (
              activeSession.partnerAvatarUrl && activeSession.areFriends ? (
                <img src={activeSession.partnerAvatarUrl} alt="avatar" />
              ) : (
                <span style={{ fontSize: '1.2rem' }}>🔍</span>
              )
            ) : (myRole === 'visitor' && (ownerInfo?.avatarUrl || activeSession.ownerAvatarUrl) ? (
              <img src={ownerInfo?.avatarUrl || activeSession.ownerAvatarUrl} alt="avatar" />
            ) : (
              myRole === 'owner' ? '🎭' : <User size={20} />
            ))}
            <span className="anon-badge">👤</span>
          </div>
          <div className="anon-partner-details">
            <h3>{partnerName}</h3>
            <p className="anon-subtitle">
              <Shield size={10} />
              {myRole === 'owner' ? 'محادثة مجهولة واردة' : 'أنت تراسل بشكل مجهول'}
            </p>
            {isTyping && <p className="anon-status-text typing">يكتب الآن...</p>}
          </div>
          {/* زر الإبلاغ */}
          <button
            className="anon-report-btn"
            onClick={() => setIsReportModalOpen(true)}
            title="إبلاغ عن هذا المستخدم"
            style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444', marginRight: '4px', flexShrink: 0 }}
          >
            <Flag size={15} />
          </button>
        </div>

        <div className="anon-messages-area">
          <div className="anon-system-msg">
            <Shield size={14} />
            {myRole === 'owner'
              ? 'هذه محادثة مجهولة. هوية المرسل مخفية.'
              : 'أنت تراسل بشكل مجهول. هويتك مخفية عن المستلم.'}
          </div>

          {messages.map((msg, idx) => {
            const isMine = msg.senderRole === myRole;
            return (
              <div 
                key={msg.id || idx} 
                id={`msg-${msg.id || msg.dbId}`}
                className={`message-wrapper ${isMine ? 'sent' : 'received'}`}
                onTouchStart={(e) => handleTouchStart(e, msg)}
                onTouchMove={(e) => handleTouchMove(e)}
                onTouchEnd={handleTouchEnd}
                style={{
                  transform: swipedMessageId === (msg.id || msg.dbId) ? `translateX(-${swipeOffset}px)` : 'none',
                  transition: swipedMessageId === (msg.id || msg.dbId) && swipeOffset !== 0 ? 'none' : 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                }}
              >
                <div 
                  className="message-bubble"
                  onClick={() => setActiveOptionMenuMessageId(prev => prev === (msg.id || msg.dbId) ? null : (msg.id || msg.dbId))}
                >
                  {/* Reply Indicator Snippet */}
                  {msg.replyTo && (
                    <div 
                      className="message-reply-snippet" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReplySnippetClick(msg.replyTo.id);
                      }}
                    >
                      <span className="reply-sender">{msg.replyTo.senderName}</span>
                      <span className="reply-text">{msg.replyTo.text}</span>
                    </div>
                  )}

                  <span className="message-text">{msg.text}</span>
                  <div className="message-meta">
                    {msg.timestamp && (
                      <span className="message-time">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    {isMine && (
                      msg.status === 'sending' ? <Clock size={12} style={{ color: '#999' }} /> :
                      msg.status === 'sent' ? <Check size={14} style={{ color: '#999' }} /> :
                      msg.status === 'delivered' ? <CheckCheck size={14} style={{ color: '#999' }} /> :
                      msg.status === 'read' ? <CheckCheck size={14} style={{ color: '#a855f7' }} /> : null
                    )}
                  </div>

                  {/* Bubble Option Menu popup overlay */}
                  {activeOptionMenuMessageId === (msg.id || msg.dbId) && (
                    <div className="message-options-menu" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="option-item" onClick={() => {
                        const isOwner = activeSession?.ownerTelegramId === userId;
                        const currentRole = isOwner ? 'owner' : 'visitor';
                        const pName = isOwner
                          ? activeSession?.visitorDisplayName
                          : (ownerInfo?.nickname || activeSession?.ownerNickname || 'مستخدم');
                        const senderName = msg.senderRole === currentRole ? 'أنا' : (pName || 'الطرف الآخر');
                        setReplyingToMessage({
                          id: msg.id || msg.dbId,
                          text: msg.text,
                          senderName
                        });
                        setActiveOptionMenuMessageId(null);
                      }}>
                        💬 الرد
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {isTyping && (
            <div className="direct-typing-indicator">
              <span></span><span></span><span></span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-wrapper-outer" style={{ position: 'relative', width: '100%' }}>
          
          {/* Active Reply Preview Bar */}
          {replyingToMessage && (
            <div className="chat-reply-preview-bar">
              <span className="reply-preview-icon">💬</span>
              <div className="reply-preview-content">
                <span className="reply-preview-sender">{replyingToMessage.senderName}</span>
                <span className="reply-preview-text">{replyingToMessage.text}</span>
              </div>
              <button 
                type="button" 
                className="reply-preview-close-btn" 
                onClick={() => setReplyingToMessage(null)}
              >
                ×
              </button>
            </div>
          )}
          {showEmojiPicker && (
            <div className="quick-emoji-picker">
              {emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="emoji-btn"
                  onClick={() => setInputText(prev => prev + emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {activeSession?.isMatch ? (
            <div className="chat-footer" style={{ padding: '16px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '14px', border: '1px solid var(--border-color)', margin: '8px' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                {activeSession.areFriends 
                  ? '🌟 أنتم أصدقاء الآن! يمكنك مراسلته من تبويب الأصدقاء.' 
                  : '🔒 هذه المحادثة هي أرشيف مطابقة عشوائية منتهية.'}
              </p>
            </div>
          ) : (
            <form className="message-input-form direct-input-form anon-input-form" onSubmit={handleSend} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: 0, borderTop: 'none', background: 'transparent' }}>
              {/* Telegram-style input pill */}
              <div className="chat-input-pill">
                <button
                  type="button"
                  className={`emoji-toggle-btn ${showEmojiPicker ? 'active' : ''}`}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  title="إضافة إيموجي"
                >
                  <Smile size={22} />
                </button>

                <input
                  type="text"
                  placeholder="اكتب رسالتك المجهولة..."
                  value={inputText}
                  onChange={handleInputChange}
                  dir="rtl"
                />
              </div>

              {/* Circle Action Button */}
              <button
                type="submit"
                className="chat-circle-action-btn send"
                title="إرسال"
                disabled={!inputText.trim()}
              >
                <Send size={18} />
              </button>
            </form>
          )}
        </div>

        {/* نافذة الإبلاغ */}
        <ReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onSubmit={handleReport}
          entityName={partnerName || 'المستخدم'}
        />
      </div>
    );
  }

  // عرض قائمة الجلسات
  return (
    <div className="anon-sessions-container">
      <div className="anon-sessions-header">
        <h2>💬 المحادثات المجهولة</h2>
      </div>

      <div className="anon-tabs">
        <button
          className={`anon-tab-btn ${sessionsTab === 'received' ? 'active' : ''}`}
          onClick={() => setSessionsTab('received')}
        >
          <Inbox size={16} />
          الواردة
        </button>
        <button
          className={`anon-tab-btn ${sessionsTab === 'sent' ? 'active' : ''}`}
          onClick={() => setSessionsTab('sent')}
        >
          <EyeOff size={16} />
          المُرسلة
        </button>
        <button
          className={`anon-tab-btn ${sessionsTab === 'match' ? 'active' : ''}`}
          onClick={() => setSessionsTab('match')}
        >
          <Search size={16} />
          المطابقة
        </button>
      </div>

      {loading ? (
        <div className="anon-loading-screen" style={{ padding: '40px 0' }}>
          <div className="loader-pulse-purple"></div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="anon-empty-sessions">
          <MessageSquare size={48} />
          <p>
            {sessionsTab === 'received'
              ? 'لا توجد محادثات مجهولة واردة بعد.\nشارك رابطك ليراسلك أحدهم!'
              : sessionsTab === 'sent'
              ? 'لم ترسل أي رسائل مجهولة بعد.'
              : 'لا توجد محادثات مطابقة عشوائية سابقة.'}
          </p>
        </div>
      ) : (
        sessions.map(session => (
          <div
            key={session._id}
            className="anon-session-card"
            onClick={() => openSession(session)}
          >
            <div className="anon-session-avatar">
              {sessionsTab === 'received' ? '🎭' : (sessionsTab === 'match' ? '🔍' : <User size={20} />)}
              {(session.unreadCount || 0) > 0 && (
                <span className="anon-session-unread-badge">{session.unreadCount}</span>
              )}
            </div>
            <div className="anon-session-info">
              <p className="anon-session-name">
                {sessionsTab === 'received'
                  ? session.visitorDisplayName
                  : (sessionsTab === 'match' ? session.partnerName : (session.ownerNickname || 'مستخدم'))}
              </p>
              <p className="anon-session-last-msg">
                {session.lastMessage || 'لا توجد رسائل'}
              </p>
            </div>
            <span className="anon-session-time">
              {session.lastMessageTimestamp
                ? new Date(session.lastMessageTimestamp).toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' })
                : ''}
            </span>
            {sessionsTab !== 'match' && (
              <button
                className="anon-session-delete"
                onClick={(e) => handleDeleteSession(e, session._id)}
                title="حذف"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default AnonymousChat;
